import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const Portfolio = () => {
  const [activeTab, setActiveTab] = useState('SEO');

  const stats = [
    {
      image: "/images/21.png",
      value: "Top 10 Results",
      description: "In just 18 months, our medical practice clients reached the top 10 results on Google's healthcare search results."
    },
    {
      image: "/images/22.png",
      value: "191.20%",
      description: "Extraordinary 191.2% increase in patient website traffic for healthcare practices."
    },
    {
      image: "/images/23.png",
      value: "6000+",
      description: "Over 6000 patient appointment conversions through targeted healthcare advertising."
    },
    {
      image: "/images/24.png",
      value: "76k",
      description: "A booming patient engagement of over 76k across medical practice social media."
    },
    {
      image: "/images/25.png",
      value: "10+ Keywords 1st",
      description: "Rank on 1st page of Google for critical healthcare search terms."
    }
  ];

  const objectives = [
    {
      image: "/images/26.png",
      title: "Establish trust and credibility for the medical practice"
    },
    {
      image: "/images/27.png",
      title: "Generate qualified patient leads and appointments"
    },
    {
      image: "/images/28.png",
      title: "Improve healthcare practice rankings on search engines"
    },
    {
      image: "/images/29.png",
      title: "Enhance patient engagement through social media"
    }
  ];

  const services = [
    {
      id: 'SEO',
      title: 'Healthcare SEO',
      description: 'Strong healthcare SEO is the foundation for any medical practice website. We tackled the challenge of building authority in a competitive healthcare market. By focusing on quality medical content and patient-focused keywords, we drove significant organic traffic. Through continuous HIPAA-compliant SEO activities and regular monitoring, we successfully brought the medical practice into Google\'s top 10 results for critical healthcare search terms.',
      image: '/images/30.png'
    },
    {
      id: 'Website',
      title: 'Medical Website Development',
      description: 'A medical practice website should inspire trust and clearly communicate healthcare services. Our client needed a HIPAA-compliant, well-structured website that showcased their medical expertise. We conducted thorough analysis of patient user experience, target demographics, and healthcare inquiry types to create an exceptionally smooth, well-navigating, responsive medical practice website that converts visitors into patients.',
      image: '/images/31.png'
    },
    {
      id: 'Social Media',
      title: 'Healthcare Social Media',
      description: 'Patients are on social media, and we leveraged these platforms to increase medical practice awareness and boost patient engagement. First, we established credibility on social media to prove the practice\'s legitimacy. Our healthcare marketing team created innovative patient education content and engaging health awareness posts while improving doctor-patient communication. From informing patients to keeping them engaged with valuable health content, we covered everything.',
      image: '/images/32.png'
    },
    {
      id: 'Paid Ads',
      title: 'Healthcare Advertising',
      description: 'Our healthcare advertising strategy focused on maximizing patient acquisition ROI through targeted HIPAA-compliant campaigns across Google Ads, Facebook, and healthcare platforms. We implemented advanced patient demographic targeting, A/B testing of medical ad creatives, and appointment conversion tracking. The result was a significant increase in qualified patient leads while maintaining competitive cost per acquisition for healthcare practices.',
      image: '/images/33.png'
    },
    {
      id: 'Branding',
      title: 'Medical Branding',
      description: 'We developed a comprehensive medical practice brand identity that resonated with patients and built trust. From professional medical logos to brand guidelines, we created a cohesive visual language that set our healthcare client apart from competing practices. Our creative team produced engaging patient-focused content that told the practice story and connected emotionally with patients seeking quality healthcare.',
      image: '/images/34.png'
    }
  ];

  const activeService = services.find(s => s.id === activeTab);

  return (
    <div className="font-['Raleway',sans-serif] bg-white text-gray-900 overflow-x-hidden pt-[90px]">
      
      <Navbar />

      {/*  HERO SECTION  */}
      <section className="relative min-h-[400px] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1920&q=80')`,
          }}
        />
        <div className="absolute inset-0 bg-[#19354a]/80" />
        
        <div className="relative z-10 text-center px-6">
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-8"
          >
            Healthcare Portfolio
          </motion.h1>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex items-center justify-center gap-4 flex-wrap"
          >
            <Link 
              to="/" 
              className="px-8 py-3 bg-[#39a5d3] text-white rounded-full font-semibold hover:bg-[#2a8db8] transition-all"
            >
              Home
            </Link>
            <span className="text-white text-2xl">|</span>
            <span className="px-8 py-3 bg-[#39a5d3] text-white rounded-full font-semibold">
              Portfolio
            </span>
          </motion.div>
        </div>
      </section>

      {/*  CASE STUDIES INTRO  */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.h2 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 mb-6"
          >
            Healthcare Case <span className="text-[#19354a]">Studies</span>
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-gray-600 text-lg leading-relaxed mb-4"
          >
            We've helped hundreds of medical practices become trusted healthcare brands in their communities. We're all about getting results for doctors and hospitals, which is why we let our patient acquisition numbers speak for themselves.
          </motion.p>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-gray-600 text-lg"
          >
            Here's just a taste of what we have achieved for our healthcare clients. If you're ready to join them,
          </motion.p>
        </div>
      </section>

      {/*  STATS SECTION  */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          {/* First Row - 3 Cards */}
          <div className="grid md:grid-cols-3 gap-6 mb-6">
            {stats.slice(0, 3).map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 text-center shadow-lg hover:shadow-xl transition-all"
              >
                <img 
                  src={stat.image} 
                  alt={stat.value}
                  className="w-16 h-16 mx-auto mb-4 object-contain"
                />
                <h3 className="text-xl lg:text-2xl font-bold text-[#19354a] mb-3">{stat.value}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{stat.description}</p>
              </motion.div>
            ))}
          </div>

          {/* Second Row - 2 Cards Centered */}
          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {stats.slice(3, 5).map((stat, index) => (
              <motion.div
                key={index + 3}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: (index + 3) * 0.1 }}
                className="bg-white rounded-2xl p-8 text-center shadow-lg hover:shadow-xl transition-all"
              >
                <img 
                  src={stat.image} 
                  alt={stat.value}
                  className="w-16 h-16 mx-auto mb-4 object-contain"
                />
                <h3 className="text-xl lg:text-2xl font-bold text-[#19354a] mb-3">{stat.value}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{stat.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/*  OBJECTIVES SECTION  */}
      <section className="relative py-20 overflow-hidden">
        {/* Blue Gradient Background */}
        <div 
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(135deg, #0f1c3f 0%, #1a2d5e 50%, #152349 100%)'
          }}
        />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <motion.h2 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-3xl lg:text-4xl xl:text-5xl font-bold text-white text-center mb-12"
          >
            Healthcare Marketing Objectives
          </motion.h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {objectives.map((objective, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 text-center hover:shadow-xl transition-all"
              >
                <img 
                  src={objective.image} 
                  alt={objective.title}
                  className="w-20 h-20 mx-auto mb-4 object-contain"
                />
                <h3 className="text-gray-900 font-semibold leading-relaxed">
                  {objective.title}
                </h3>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/*  OUR APPROACH SECTION  */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            
            {/* Left - Image */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img 
                 src="/images/About-us.png"  
                alt="Our Healthcare Approach"
                className="w-full max-w-[500px] h-auto mx-auto rounded-2xl"
              />
            </motion.div>

            {/* Right - Content */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h2 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 mb-6">
                Our Healthcare Marketing <span className="text-[#19354a]">Approach</span>
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Our main strategy was to create a healthcare marketing roadmap that would establish the medical practice as a professional, reputable, and credible healthcare brand. To accomplish these results, we worked on increasing patient awareness, appointment conversions, and qualified patient lead generation across all major social media platforms like Facebook, Instagram, LinkedIn, and obviously on Google. At the same time, we maintained brand consistency with HIPAA compliance to further increase credibility and strengthen patient trust in the medical practice.
              </p>
            </motion.div>

          </div>
        </div>
      </section>

      {/*  WHAT DID WE DO SECTION  */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          
          {/* Section Header */}
          <motion.h2 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-3xl lg:text-4xl xl:text-5xl font-bold text-center mb-12"
          >
            What did <span className="text-[#19354a]">we do?</span>
          </motion.h2>

          {/* Tabs */}
          <div className="bg-[#19354a] rounded-t-2xl p-4">
            <div className="flex flex-wrap justify-center gap-2">
              {services.map((service) => (
                <button
                  key={service.id}
                  onClick={() => setActiveTab(service.id)}
                  className={`px-6 py-3 rounded-full font-semibold transition-all ${
                    activeTab === service.id
                      ? 'bg-white text-[#19354a]'
                      : 'bg-transparent text-white hover:bg-white/20'
                  }`}
                >
                  {service.id}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="bg-[#19354a] rounded-b-2xl p-8 lg:p-12">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="grid lg:grid-cols-2 gap-8 items-center"
              >
                {/* Left - Content */}
                <div>
                  <h3 className="text-2xl lg:text-3xl font-bold text-white mb-6">
                    {activeService?.title}
                  </h3>
                  <p className="text-white/90 leading-relaxed">
                    {activeService?.description}
                  </p>
                </div>

                {/* Right - Image */}
                <div className="flex justify-center">
                  <img 
                    src={activeService?.image} 
                    alt={activeService?.title}
                    className="w-full max-w-[450px] h-auto rounded-xl"
                  />
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/*  CTA SECTION  */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.h2 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6"
          >
            Ready to Be Our Next Healthcare Success Story?
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-gray-600 text-lg mb-8"
          >
            Let's discuss how we can help you achieve similar patient acquisition results for your medical practice.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Link 
              to="/contact" 
              className="px-8 py-4 bg-[#19354a] text-white rounded-lg font-semibold hover:bg-[#0f2133] transition-all"
            >
              Start Your Healthcare Project
            </Link>
            <a 
              href="tel:+918920845297" 
              className="px-8 py-4 border-2 border-[#19354a] text-[#19354a] rounded-lg font-semibold hover:bg-[#19354a] hover:text-white transition-all"
            >
              Call Us Now
            </a>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Portfolio;